import { useCallback, useEffect, useState } from 'react';

import { api, type TermekLista, type TermekSor } from '../api.ts';
import { ft } from '../formazas.ts';
import { Mezo, SzamMezo, ValasztoMezo } from '../reszek/Mezo.tsx';

/**
 * Termékek és árak. (Megbízás 2.0 / 5.8)
 *
 * Eddig az árak szétszórva éltek: a moslék ára a partner nevébe volt írva,
 * a fáradt olajé kézzel számolt összegként. Innentől egy helyen állnak, és
 * innen dolgozik a szerződés, az ellenőrzés és később a számlázás.
 *
 * **Az ár átírása nem hat vissza.** A lezárt napok és a kiadott szerződések
 * a saját, akkori értéküket tartják.
 */

const TIPUS_NEVEK: Record<string, string> = {
  bevetel: 'bevétel',
  kiadas: 'kiadás',
  betetdij: 'betétdíj',
  adat: 'adat',
};

/** Mit jelent a típus — a felület ezt írja ki magyarázatként. */
const TIPUS_MAGYARAZAT: Record<string, string> = {
  bevetel: 'Pénzt kapunk érte (pl. a moslék elszállításáért).',
  kiadas: 'Mi fizetünk érte (pl. a használt sütőolaj átvételéért).',
  betetdij: 'Göngyöleg betétdíja — a szerződésben szerepel.',
  adat: 'Nem pénz, csak egy rögzített szám a szerződés szövegéhez.',
};

export function Termekek() {
  const [lista, setLista] = useState<TermekLista | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [uzenet, setUzenet] = useState<string | null>(null);
  const [ujNyitva, setUjNyitva] = useState(false);

  const betolt = useCallback(async () => {
    try {
      setLista(await api.get<TermekLista>('/api/termek'));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a termékeket.');
    }
  }, []);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  /**
   * Egy mező mentése. A szerver a frissített terméket küldi vissza, hogy a
   * számított nettó/bruttó is stimmeljen — nem töltjük újra az egész listát.
   */
  const ment = useCallback(async (id: number, mezo: string, ertek: unknown) => {
    const frissitett = await api.patch<TermekSor>(`/api/termek/${id}/mezo/${mezo}`, { ertek });
    setLista((elozo) =>
      elozo
        ? { ...elozo, sorok: elozo.sorok.map((t) => (t.id === id ? frissitett : t)) }
        : elozo,
    );
  }, []);

  async function torol(t: TermekSor) {
    if (!confirm(`Törlöd a(z) "${t.nev}" terméket?`)) return;
    try {
      await api.torol(`/api/termek/${t.id}`);
      setUzenet(`${t.nev} törölve.`);
      await betolt();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A törlés nem sikerült.');
    }
  }

  const sorok = lista?.sorok ?? [];
  const tipusLehetosegek = (lista?.tipusok ?? []).map((t) => ({
    ertek: t,
    cimke: TIPUS_NEVEK[t] ?? t,
  }));

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Termékek és árak</span>
        <span className="hint">
          Ezekből dolgozik a szerződés, az ellenőrzés és később a számlázás. <b>Az elszámolás
          mindenhol a bruttó árral számol</b> (a HSO nettó 150 Ft/kg = bruttó 190,5 Ft/kg); a
          szerződésekbe a nettó ár kerül.
        </span>
      </div>

      {hiba && (
        <div className="uzenet bad" onClick={() => setHiba(null)}>
          {hiba}
        </div>
      )}
      {uzenet && (
        <div className="uzenet ok" onClick={() => setUzenet(null)}>
          {uzenet}
        </div>
      )}

      <div className="actbar">
        <button className="btn sm" onClick={() => setUjNyitva((v) => !v)}>
          {ujNyitva ? '× Mégsem' : '+ Új termék'}
        </button>
        <span className="hint">
          <b>Az árváltozás nem hat vissza:</b> a már lezárt napok és a kiadott szerződések a
          saját, akkori árukat tartják.
        </span>
      </div>

      {ujNyitva && (
        <UjTermek
          tipusLehetosegek={tipusLehetosegek}
          onKesz={() => {
            setUjNyitva(false);
            void betolt();
          }}
          onMegse={() => setUjNyitva(false)}
        />
      )}

      <div className="matrix">
        <table className="tt2">
          <thead>
            <tr>
              <th style={{ width: 90 }}>Kód</th>
              <th>Megnevezés</th>
              <th style={{ width: 90 }}>Egység</th>
              <th className="n" style={{ width: 100 }}>
                Ár
              </th>
              <th style={{ width: 90 }}>Ár típusa</th>
              <th className="n" style={{ width: 70 }}>
                Áfa
              </th>
              <th className="n" style={{ width: 100 }}>
                Nettó
              </th>
              <th className="n" style={{ width: 100 }}>
                Bruttó
              </th>
              <th style={{ width: 110 }}>Típus</th>
              <th>Megjegyzés</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorok.map((t) => (
              <tr key={t.id} className={t.aktiv ? undefined : 'gyr'}>
                <td className="nm3">
                  {t.vedett ? (
                    <>
                      <b>{t.kod}</b>
                      <small title="A program számol vele, ezért a kódja nem írható át.">
                        alaptermék
                      </small>
                    </>
                  ) : (
                    <Mezo ertek={t.kod} onMent={(v) => ment(t.id, 'kod', v)} />
                  )}
                </td>
                <td>
                  <Mezo ertek={t.nev} onMent={(v) => ment(t.id, 'nev', v)} />
                </td>
                <td>
                  <Mezo ertek={t.egyseg} onMent={(v) => ment(t.id, 'egyseg', v)} />
                </td>
                <td>
                  <SzamMezo ertek={t.ar} onMent={(v) => ment(t.id, 'ar', v)} />
                </td>
                <td>
                  <ValasztoMezo
                    ertek={t.brutto ? '1' : '0'}
                    lehetosegek={[
                      { ertek: '0', cimke: 'nettó' },
                      { ertek: '1', cimke: 'bruttó' },
                    ]}
                    kotelezo
                    onMent={(v) => ment(t.id, 'brutto', v === '1')}
                  />
                </td>
                <td>
                  <SzamMezo ertek={t.afa_kulcs} onMent={(v) => ment(t.id, 'afa_kulcs', v)} />
                </td>
                <td className="calc2">{t.netto !== null ? ft(t.netto) : '—'}</td>
                <td className="calc2 tk-szamolt">
                  {t.brutto_ertek !== null ? ft(t.brutto_ertek) : '—'}
                </td>
                <td>
                  <ValasztoMezo
                    ertek={t.tipus}
                    lehetosegek={tipusLehetosegek}
                    kotelezo
                    onMent={(v) => ment(t.id, 'tipus', v)}
                  />
                </td>
                <td>
                  <Mezo ertek={t.megjegyzes} onMent={(v) => ment(t.id, 'megjegyzes', v)} />
                </td>
                <td className="act">
                  {t.vedett ? (
                    <span
                      className="halvany"
                      title="Ezt a terméket a program használja, nem törölhető."
                    >
                      —
                    </span>
                  ) : (
                    <button className="xb" title="Törlés" onClick={() => void torol(t)}>
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={11} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  Betöltés…
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="uzenet flag" style={{ marginTop: 14 }}>
        A <b>nettó</b> és a <b>bruttó</b> oszlop számolt érték: attól függően, hogy az árat
        nettóban vagy bruttóban vezetjük, a másikat a program adja hozzá az áfakulcsból. Az
        „adat" típusú soroknál (például a hordó mérete) nincs értelme árnak, ott gondolatjel áll.
      </div>

      <p className="lbl" style={{ marginTop: 14 }}>
        Honnan jön melyik ár
      </p>
      <div className="matrix">
        <table className="tt2">
          <tbody>
            {[
              ['Olaj alapár és F+ ár', 'A partnertörzsből, étterem soronként (Partnerek fül).'],
              ['Raktári (R) ár', 'A partnertörzsből, étterem soronként.'],
              [
                'Moslék',
                'A partner egyedi ára, ha van (Partnerek fül, „MO ár" oszlop); egyébként az itteni MO alapár.',
              ],
              ['FO / HSO, hordó, kanna', 'Innen, a Termékek közül — mindenkinél egységesen.'],
            ].map(([mi, honnan]) => (
              <tr key={mi}>
                <td className="nm3" style={{ width: 200 }}>
                  <b>{mi}</b>
                </td>
                <td style={{ padding: '7px 10px', color: 'var(--muted)' }}>{honnan}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="lbl" style={{ marginTop: 14 }}>
        Mit jelentenek a típusok
      </p>
      <div className="matrix">
        <table className="tt2">
          <tbody>
            {Object.entries(TIPUS_MAGYARAZAT).map(([kulcs, szoveg]) => (
              <tr key={kulcs}>
                <td className="nm3" style={{ width: 110 }}>
                  <b>{TIPUS_NEVEK[kulcs] ?? kulcs}</b>
                </td>
                <td style={{ padding: '7px 10px', color: 'var(--muted)' }}>{szoveg}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */

function UjTermek({
  tipusLehetosegek,
  onKesz,
  onMegse,
}: {
  tipusLehetosegek: Array<{ ertek: string; cimke: string }>;
  onKesz: () => void;
  onMegse: () => void;
}) {
  const [kod, setKod] = useState('');
  const [nev, setNev] = useState('');
  const [egyseg, setEgyseg] = useState('db');
  const [ar, setAr] = useState('');
  const [brutto, setBrutto] = useState('0');
  const [afa, setAfa] = useState('27');
  const [tipus, setTipus] = useState('bevetel');
  const [hiba, setHiba] = useState<string | null>(null);
  const [munkaban, setMunkaban] = useState(false);

  async function felvesz() {
    setMunkaban(true);
    setHiba(null);
    try {
      await api.post('/api/termek', {
        kod,
        nev,
        egyseg,
        ar,
        brutto: brutto === '1',
        afa_kulcs: afa,
        tipus,
      });
      onKesz();
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A felvétel nem sikerült.');
      setMunkaban(false);
    }
  }

  return (
    <div className="tdet" style={{ background: '#fff', borderColor: 'var(--rule2)' }}>
      <h4>Új termék</h4>
      {hiba && <div className="uzenet bad">{hiba}</div>}
      <div className="qgrid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <label>
          Kód
          <input value={kod} onChange={(e) => setKod(e.target.value)} placeholder="pl. HORDO2" />
        </label>
        <label>
          Egység
          <input value={egyseg} onChange={(e) => setEgyseg(e.target.value)} />
        </label>
        <label>
          Ár
          <input value={ar} onChange={(e) => setAr(e.target.value)} />
        </label>
        <label>
          Áfa %
          <input value={afa} onChange={(e) => setAfa(e.target.value)} />
        </label>
      </div>
      <div className="qgrid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
        <label>
          Megnevezés
          <input
            value={nev}
            onChange={(e) => setNev(e.target.value)}
            style={{ textAlign: 'left', fontFamily: 'inherit' }}
          />
        </label>
        <label>
          Ár típusa
          <select value={brutto} onChange={(e) => setBrutto(e.target.value)}>
            <option value="0">nettó</option>
            <option value="1">bruttó</option>
          </select>
        </label>
        <label>
          Típus
          <select value={tipus} onChange={(e) => setTipus(e.target.value)}>
            {tipusLehetosegek.map((t) => (
              <option key={t.ertek} value={t.ertek}>
                {t.cimke}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="btns">
        <button className="btn pri sm" disabled={munkaban || !kod || !nev} onClick={() => void felvesz()}>
          Felvétel
        </button>
        <button className="btn sm" onClick={onMegse}>
          Mégsem
        </button>
      </div>
    </div>
  );
}
